"use client";

import { isDevMode, TETHER_SYMBOL } from "@/constants";
import { BinanceService } from "@/services/binance/binance-service";
import { BinanceWsService } from "@/services/binance/binance-ws-service";
import {
  BinanceWsKlineResponse,
  BinanceWsStream,
} from "@/services/binance/schema";
import React, { PropsWithChildren } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/shallow";

const verbose = !false;
function verboseLog(...args: any[]) {
  if (verbose && isDevMode) {
    console.log(args.join(" "));
  }
}

export type BinanceLivePrices = {
  [key: string]: {
    close: number;
    open: number;
    timestamp: number;
  };
};

type BinanceWsContextType = {
  binanceWsService: typeof BinanceWsService;
  binanceLivePrices: BinanceLivePrices;
  symbols: Set<string>;
   
  addSymbol: (symbol: string | string[]) => void;
   
  removeSymbol: (symbol: string | string[]) => void;
};

export const useBinanceWsStore = create<BinanceWsContextType>((set, get) => ({
  binanceWsService: BinanceWsService,
  binanceLivePrices: {},
  symbols: new Set(),
  addSymbol: (symbol: string | string[]) => {
    const newSet = new Set(get().symbols);

    if (Array.isArray(symbol)) {
      symbol.forEach((s) => newSet.add(s));
    } else {
      newSet.add(symbol);
    }

    set({ symbols: newSet });
  },
  removeSymbol: (symbol: string | string[]) => {
    const newSet = new Set(get().symbols);

    if (Array.isArray(symbol)) {
      symbol.forEach((s) => newSet.delete(s));
    } else {
      newSet.delete(symbol);
    }

    set({ symbols: newSet });
  },
}));

export const BinanceWsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const symbols = useBinanceWsStore(useShallow((state) => state.symbols));

  React.useEffect(() => {
    const binanceWsService = useBinanceWsStore.getState().binanceWsService;

    binanceWsService.openConnection();
    return () => {
      binanceWsService.closeConnection();
    };
  }, []);

  React.useEffect(() => {
    if (symbols.size === 0) {
      return;
    }
    verboseLog(
      "[BWS_PROVIDER] Attempting to subscribe to [",
      symbols.size,
      "] assets",
    );
    const binanceWsService = useBinanceWsStore.getState().binanceWsService;

    let subscribedStreams: BinanceWsStream[] = [];
    const bootstrap = async () => {
      await Promise.allSettled(
        Array.from(symbols.values()).map(async (symbol) => {
          const isSupported = await BinanceService.isAssetSupported(symbol);
          if (!isSupported) {
            return;
          }

          const stream =
            `${symbol.toLowerCase()}usdt@kline_1s` as BinanceWsStream;
          subscribedStreams.push(stream);
        }),
      );

      if (subscribedStreams.length === 0) {
        verboseLog("[BWS_PROVIDER] No supported assets to subscribe to");
        return;
      }

      verboseLog("[BWS_PROVIDER] Subscribing to streams: ", subscribedStreams);
      binanceWsService.subscribeToWebSocketStream(
        subscribedStreams,
        (data: BinanceWsKlineResponse) => {
          const {
            s: pair,
            k: { t: openTime, o: openPrice, c: closePrice },
          } = data;
          const symbol = pair.replace(TETHER_SYMBOL, "");

          useBinanceWsStore.setState((state) => {
            const binanceLivePrices = { ...state.binanceLivePrices };
            binanceLivePrices[symbol] = {
              close: parseFloat(closePrice),
              open: parseFloat(openPrice),
              timestamp: openTime,
            };

            return { binanceLivePrices };
          });
        },
      );
    };

    bootstrap();

    verboseLog("STREAMS: ", JSON.stringify(binanceWsService.streams, null, 2));

    return () => {
      if (subscribedStreams.length === 0) {
        return;
      }
      verboseLog("Unsubscribing from streams: ", subscribedStreams);
      binanceWsService.unsubscribeFromWebSocketStream(subscribedStreams);
    };
  }, [symbols]);

  return children;
};
